/**
 * Pre-signed storage urls carry their credentials in the query string
 * (`X-Amz-Signature`, `X-Amz-Security-Token`, `X-Goog-Signature`). They are
 * bearer credentials: anyone who can read them can fetch the object until they
 * expire. Telemetry is not a place to keep those, so the query string is
 * stripped before anything leaves the browser.
 *
 * The decision is made on the signing markers rather than on a host, so a url
 * stays covered no matter which storage backend issued it.
 */

/** Any url with a query string. Matched first, inspected second — a single
 * pattern doing both would nest quantifiers and backtrack badly. */
const URL_WITH_QUERY = /(https?:\/\/[^\s"'<>?]+)\?([^\s"'<>]+)/g;

/** Stateless on purpose: a `g` flag here would carry `lastIndex` between calls. */
const SIGNING_MARKER = /X-Amz-Signature|X-Amz-Security-Token|X-Goog-Signature/i;

/**
 * Replaces the query string of every pre-signed url found in `text` with
 * `?<redacted>`, and leaves everything else untouched. Works on a bare url as
 * well as on a message that embeds one (a serialized `HttpErrorResponse`, for
 * instance, carries the url more than once).
 */
export function redactSignedUrls<T extends string | undefined>(text: T): T {
  if (typeof text !== 'string') {
    return text;
  }

  return text.replace(URL_WITH_QUERY, (match, base: string, query: string) =>
    SIGNING_MARKER.test(query) ? `${base}?<redacted>` : match,
  ) as T;
}
